import React, { FC, ReactNode, useEffect, useState } from 'react';
import { Card } from '../../types';
import {
  Box,
  BoxProps,
  SvgIconProps,
  Tooltip,
  Typography,
  styled,
  useTheme,
} from '@mui/material';

import { MotionProps, motion } from 'framer-motion';
import { BhabiStorage } from '../../../../shared/lib';
import { useSelector } from 'react-redux';
import { Store } from '../../../../redux';
import {
  ClubsIcon,
  DiamondsIcon,
  HeartsIcon,
  SpadesIcon,
} from '@/assets/card-icons';
import { cardLabel } from '@/screens/guest/lib/turn';

export interface CommonDeckCardProps {
  card: Card;
}

type CardSuitsObject = {
  [key in Card['suite']]: (props?: SvgIconProps) => ReactNode;
};

export const symbol: CardSuitsObject = {
  spades: props => <SpadesIcon {...props} />, //'♠',
  hearts: props => <HeartsIcon {...props} />, //'♥',
  diamonds: props => <DiamondsIcon {...props} />, // '♦',
  clubs: props => <ClubsIcon {...props} />,
};

const useCardColor = () => {
  const theme = useTheme();

  const color: { [key in Card['suite']]: string } = {
    spades: theme.palette.grey[900],
    hearts: theme.palette.error.main,
    diamonds: theme.palette.error.main,
    clubs: theme.palette.grey[900],
  };

  return (suite: Card['suite']) => color[suite];
};

export interface PaperCardProps extends CommonDeckCardProps {
  disabled?: boolean;
  cardRef?: React.LegacyRef<HTMLDivElement>;
}
export const PaperCard: FC<PaperCardProps> = ({ card, disabled, cardRef }) => {
  const cardColor = useCardColor();

  return (
    <div ref={cardRef}>
      <PaperCardWrapper
        className="bh-card-paper"
        cardColor={cardColor(card.suite)}
        height={CARD_HEIGHT}
        width={CARD_WIDTH}
        sx={
          disabled
            ? {
                // Dark: darker, muted tan — clearly duller than the active
                // cream (#C8B896) but still warm-toned, not blue.
                // Light: muted greige, visibly dimmer than the active #FAFAFA.
                backgroundColor: theme =>
                  theme.palette.mode === 'dark' ? '#7A6A52' : '#C8BFB0',
                cursor: 'not-allowed',
              }
            : undefined
        }
      >
        <Typography component="span">
          {symbol[card.suite]({ className: 'card-suit' })}
        </Typography>
        <CornerCardValue
          card={card}
          cardColor={cardColor(card.suite)}
          placement="topLeft"
        />
        <CornerCardValue
          card={card}
          cardColor={cardColor(card.suite)}
          placement="bottomRight"
          boxProps={{ flexDirection: 'column-reverse' }}
        />
      </PaperCardWrapper>
    </div>
  );
};

interface CornerCardValueProps extends CommonDeckCardProps {
  cardColor: string;
  placement: 'topLeft' | 'bottomRight';
  boxProps?: BoxProps;
}

const CornerCardValue: FC<CornerCardValueProps> = ({
  card: { value, suite },
  cardColor,
  placement,
  boxProps,
}) => (
  <Box
    display="flex"
    flexDirection="column"
    height={60}
    sx={{
      color: cardColor,
      position: 'absolute',
      ...(placement === 'bottomRight'
        ? {
            bottom: 0,
            right: 5,
            textAlign: 'right',
          }
        : {
            top: 0,
            left: 5,
          }),
    }}
    {...boxProps}
  >
    <Typography fontSize={CARD_FONT} fontWeight={800}>
      {value.trim()}
    </Typography>
    <Typography component="span">
      {symbol[suite]({ style: { height: 15, width: 15 } })}
    </Typography>
  </Box>
);

interface PaperCardWrapperProps {
  cardColor: string;
}

export const CARD_HEIGHT = {
  md: 100,
  sm: 90,
  lg: 170,
  xs: 90,
};
export const CARD_WIDTH = { md: 75, sm: 70, lg: 110, xs: 60 };
export const CARD_BORDER_RADIUS = 8;
export const CARD_FONT = {
  sm: '1rem',
  lg: '1.5rem',
};

const PaperCardWrapper = styled(Box, {
  shouldForwardProp: prop => prop !== 'cardColor',
})<PaperCardWrapperProps>(({ theme, cardColor }) => ({
  position: 'relative',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: 'center',
  padding: theme.spacing(1),
  // Dark mode: dull warm cream — easy on the eyes, clearly distinct from the
  // disabled state (grey[400] = muted purple-gray in the VOID palette).
  // Light mode: near-white, consistent with classic playing card appearance.
  border: `1px solid ${theme.palette.mode === 'dark' ? '#4A3E2A' : theme.palette.grey[400]}`,
  backgroundColor: '#fff9ec',
  backgroundImage:
    'linear-gradient(135deg, rgba(255,255,255,.92), transparent 48%, rgba(173,145,98,.12))',
  boxShadow:
    '0 1px 0 #fff, 0 2px 0 #d7cbb5, 0 3px 0 #b9a88b, 1px 6px 9px rgba(0,0,0,.32)',
  '&::after': {
    content: '""',
    position: 'absolute',
    inset: 3,
    border: '1px solid rgba(107,83,49,.08)',
    borderRadius: 5,
    pointerEvents: 'none',
  },
  cursor: 'pointer',
  borderRadius: CARD_BORDER_RADIUS,
  '& .card-suit': {
    color: cardColor,
    height: '1.5rem',
    width: '1.5rem',
    [theme.breakpoints.up('lg')]: {
      height: '3rem',
      width: '3rem',
    },
    fontWeight: 'bold',
  },
}));

export interface DeckCardProps extends PaperCardProps {
  motionDivProps?: MotionProps;
  tooltip?: boolean;
  onClick?: () => void;
  cardRef?: React.LegacyRef<HTMLDivElement>;
}

export const DeckCard: FC<DeckCardProps> = ({
  motionDivProps,
  onClick,
  tooltip,
  ...props
}) => {
  const [showTooltip, setShowTooltip] = useState(!!tooltip);
  const {
    gameInfo: { room },
  } = useSelector((state: Store) => state.inPlayReducer);

  useEffect(() => {
    BhabiStorage.getItem(res => {
      if ((res && res === room?.roomID) || !tooltip || !room?.roomID) {
        setShowTooltip(false);
        return;
      }

      setTimeout(() => {
        setShowTooltip(false);
        BhabiStorage.setItem('deckCardTooltip', room?.roomID);
      }, 3000);
    }, 'deckCardTooltip');
  }, [tooltip, room?.roomID]);

  return (
    <Tooltip
      title="Click on this card to throw it."
      arrow
      placement="top"
      open={showTooltip}
    >
      <motion.div
        onClick={props.disabled ? undefined : onClick}
        {...(onClick
          ? {
              role: 'button',
              tabIndex: props.disabled ? -1 : 0,
              'aria-disabled': props.disabled || undefined,
              'aria-label': `${tooltip ? 'Play ' : ''}${cardLabel(props.card)}${
                props.disabled ? ', not playable now' : ''
              }`,
              'data-card-state': props.disabled ? 'blocked' : 'playable',
              className: 'bh-card-hit',
              onKeyDown: (e: React.KeyboardEvent) => {
                if (props.disabled) return;
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  onClick();
                }
              },
            }
          : { 'aria-label': cardLabel(props.card), role: 'img' })}
        {...motionDivProps}
      >
        <PaperCard {...props} />
      </motion.div>
    </Tooltip>
  );
};
